import { createSelector } from 'reselect';
import { CallbackSaga, createGeneralCall } from '@altermaind/core/requests-status-management';
import { IS_MOCK_ENABLED } from '@/_shared/constants/mock';
import { GenericGet } from '@/_shared/types';
import { getCurrentOrganization } from '@/core/state/selectors';
import { API_ORGANIZATIONS_ORGANIZATIONID_CREDITS_USER_PATH_KEY } from '@/swagger/constants/credits-keys';
import { API_GET_EMPLOYER_CREDITS, KEY_GET_EMPLOYER_CREDITS } from '../constants';
import { MOCK_RESPONSE_GET_EMPLOYER_CREDITS } from '../mocks';

type RequestGetEmployerCredits = GenericGet<
  typeof API_ORGANIZATIONS_ORGANIZATIONID_CREDITS_USER_PATH_KEY
>['request']['path'];

type ResponseGetEmployerCredits = GenericGet<
  typeof API_ORGANIZATIONS_ORGANIZATIONID_CREDITS_USER_PATH_KEY
>['response']['body'];

export type CallParamsGetEmployerCredits = Omit<RequestGetEmployerCredits, 'organizationId'> & {
  successSaga?: CallbackSaga;
  errorSaga?: CallbackSaga;
};

type CallResponseGetEmployerCredits = ResponseGetEmployerCredits;

type CallErrorGetEmployerCredits = {
  message: string;
  code: number;
};

type CallSelectorsGetEmployerCredits = {
  monthlyCredits?: number;
  consumedCredits?: number;
  totalAvailableCredits?: number;
  totalCredits?: number;
};

export const getEmployerCreditsCall = createGeneralCall<
  CallParamsGetEmployerCredits,
  CallResponseGetEmployerCredits,
  CallErrorGetEmployerCredits,
  CallSelectorsGetEmployerCredits
>({
  id: KEY_GET_EMPLOYER_CREDITS,
  url: () => state => {
    const org = getCurrentOrganization(state);
    return API_GET_EMPLOYER_CREDITS(org as unknown as string);
  },
  paramsSelector:
    ({ payload }) =>
    () => {
      return payload;
    },
  method: 'get',
  successMock: IS_MOCK_ENABLED() ? MOCK_RESPONSE_GET_EMPLOYER_CREDITS : undefined,
  selectors: {
    totalAvailableCredits: ({ selectors }) =>
      createSelector(selectors.response, res => res?.totalAvailableCredits || 0),
    monthlyCredits: ({ selectors }) =>
      createSelector(selectors.response, res => res?.monthlyCredits || 0),
    consumedCredits: ({ selectors }) =>
      createSelector(selectors.response, res => res?.consumedCredits || 0),
    totalCredits: ({ selectors }) =>
      createSelector(selectors.response, res => res?.totalCredits || 0),
  },
});
